import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MainComponent } from './components/main/main.component';
import { AuthGuard } from './guards/auth/auth.guard';
import { NotFoundPageComponent } from './pages/public/404/404.component';
import { AccountPageComponent } from './pages/account/account.component';
import { DashboardPageComponent } from './pages/dashboard/dashboard.component';
import { LandingPageComponent } from './pages/public/landing/landing.component';
import { ProjectEditorPageComponent } from './pages/project-editor/project-editor.component';
import { ProjectApiKeysPageComponent } from './pages/project-api-keys/project-api-keys.component';
import { ProjectNotificationsPageComponent } from './pages/project-notifications/project-notifications.component';
import { ProjectAnalyticsPageComponent } from './pages/project-analytics/project-analytics.component';
import { ProjectErrorsPageComponent } from './pages/project-errors/project-errors.component';
import { ProjectOverviewPageComponent } from './pages/project-overview/project-overview.component';
import { ProjectErrorDetailPageComponent } from './pages/project-error-detail/project-error-detail.component';
import { ProjectTracingPageComponent } from './pages/project-tracing/project-tracing.component';
import { ProjectTraceDetailPageComponent } from './pages/project-trace-detail/project-trace-detail.component';
import { ProjectStatusPageComponent } from './pages/project-status-page/project-status-page.component';
import { UserLoginComponent } from './pages/public/user-login/user-login.component';
import { UserRegisterComponent } from './pages/public/user-register/user-register.component';
import { AdminDashboardPageComponent } from './pages/admin-dashboard/admin-dashboard.component';
import { UserForgotPasswordComponent } from './pages/public/user-forgot-password/user-forgot-password.component';
import { UserResetPasswordComponent } from './pages/public/user-reset-password/user-reset-password.component';
import { LegalPageComponent } from './pages/public/legal/legal.component';
import { InvitationComponent } from './pages/public/invitation/invitation.component';
import { RequestAccessComponent } from './pages/public/request-access/request-access.component';
import { OrganizationPageComponent } from './pages/organization/organization.component';
import { TeamsPageComponent } from './pages/teams/teams.component';
import { KNOWLEDGE_BASE_ARTICLES } from './pages/public/knowledge-base/knowledge-base.catalog';
import { knowledgeBaseNotFoundGuard } from './pages/public/knowledge-base/knowledge-base-not-found.guard';

const routes: Routes = [
  {
    path: '',
    component: LandingPageComponent,
  },
  {
    path: 'login',
    component: UserLoginComponent,
  },
  {
    path: 'register',
    component: UserRegisterComponent,
  },
  {
    path: 'forgot-password',
    component: UserForgotPasswordComponent,
  },
  {
    path: 'reset-password',
    component: UserResetPasswordComponent,
  },
  {
    path: 'imprint',
    component: LegalPageComponent,
    title: 'Imprint · PulseWatch',
    data: { page: 'imprint', seo: { description: 'Legal notice of PulseWatch, a service of SM Tech GmbH, Winterthur, Switzerland.' } },
  },
  {
    path: 'impressum',
    redirectTo: 'imprint',
  },
  {
    path: 'terms',
    component: LegalPageComponent,
    title: 'Terms of Service · PulseWatch',
    data: { page: 'terms', seo: { description: 'Terms of Service for PulseWatch uptime monitoring, status pages, error tracking and tracing.' } },
  },
  {
    path: 'privacy',
    component: LegalPageComponent,
    title: 'Privacy Policy · PulseWatch',
    data: { page: 'privacy', seo: { description: 'How PulseWatch (SM Tech GmbH) processes personal data, where it is stored and your rights.' } },
  },
  {
    path: 'invite/:token',
    component: InvitationComponent,
  },
  {
    path: 'request-access',
    component: RequestAccessComponent,
  },
  {
    path: 'main',
    canActivate: [AuthGuard],
    data: { loginPath: '/login' },
    component: MainComponent,
    children: [
      {
        path: 'dashboard',
        component: DashboardPageComponent,
      },
      {
        path: 'account',
        component: AccountPageComponent,
      },
      {
        path: 'new-project',
        component: ProjectEditorPageComponent,
      },
      {
        path: 'project/:uuid',
        component: ProjectOverviewPageComponent,
      },
      {
        path: 'project/:uuid/settings',
        component: ProjectEditorPageComponent,
      },
      {
        path: 'organization',
        component: OrganizationPageComponent,
      },
      {
        path: 'organization/teams',
        component: TeamsPageComponent,
      },
      {
        path: 'api-keys',
        component: ProjectApiKeysPageComponent,
        data: { scope: 'organization' },
      },
      {
        path: 'project/:uuid/api-keys',
        component: ProjectApiKeysPageComponent,
      },
      {
        path: 'project/:uuid/notifications',
        component: ProjectNotificationsPageComponent,
      },
      {
        path: 'project/:uuid/analytics',
        component: ProjectAnalyticsPageComponent,
      },
      {
        path: 'project/:uuid/errors',
        component: ProjectErrorsPageComponent,
      },
      {
        path: 'project/:uuid/errors/:issueUuid',
        component: ProjectErrorDetailPageComponent,
      },
      {
        path: 'project/:uuid/tracing',
        component: ProjectTracingPageComponent,
      },
      {
        path: 'project/:uuid/tracing/:transactionUuid',
        component: ProjectTraceDetailPageComponent,
      },
      {
        path: 'status/:projectSlug',
        component: ProjectStatusPageComponent,
      },
      {
        path: 'admin/dashboard',
        component: AdminDashboardPageComponent,
        canActivate: [AuthGuard],
        data: { needAdminAccess: true, loginPath: '/login' },
      },
      {
        path: '**',
        redirectTo: 'dashboard'
      },
    ]
  },
  {
    path: 'status/:projectSlug',
    component: ProjectStatusPageComponent,
  },
  {
    // Public, unauthenticated help centre. Every article is its own lazy chunk; the
    // routes come from the generated catalog, so adding an article needs no edit here.
    path: 'knowledge-base',
    loadComponent: () =>
      import('./pages/public/knowledge-base/knowledge-base-layout.component').then((m) => m.KnowledgeBaseLayoutComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'Knowledge base · PulseWatch',
        data: {
          seo: {
            description: 'PulseWatch knowledge base: guides for status pages, uptime checks, error tracking, tracing and the SDKs.',
            keywords: ['PulseWatch', 'knowledge base', 'help', 'documentation', 'status page', 'error tracking'],
          },
        },
        loadComponent: () =>
          import('./pages/public/knowledge-base/knowledge-base.component').then((m) => m.KnowledgeBaseComponent),
      },
      ...KNOWLEDGE_BASE_ARTICLES.map((article) => ({
        path: article.route_path,
        title: `${article.title} · PulseWatch`,
        data: { seo: { description: article.summary, keywords: article.keywords } },
        loadComponent: article.loadComponent,
      })),
      { path: '**', canActivate: [knowledgeBaseNotFoundGuard], children: [] },
    ],
  },
  {
    path: '**',
    component: NotFoundPageComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
